/*
  The forms for adding and changing an element by hand (7.10). Each kind of element has one spec: its empty form, how
  an existing element fills the form, what is wrong with the answers (in plain words), and the content it sends.
  The pages around the spec (the add page and the edit page) add the name, the definition and the "needed by" line.

  Checks here are for comfort: the server checks every rule again.
*/

import type { ComponentType } from "react";

import type { AddByHandDraft, ElementRow, Id, Json, LineChange, LineDraft, LineRow } from "../../../api/types";
import type { AddableKind } from "../lib/words";

export type Values = Record<string, unknown>;
/** Plain-word problems by field name; empty when the answers are fine. */
export type Errors = Record<string, string>;
export type Mode = "add" | "edit";

export interface FieldsProps<V extends Values = Values> {
  values: V;
  set: (patch: Partial<V>) => void;
  errors: Errors;
  mode: Mode;
  /** The element being changed, in edit mode. */
  elementId?: Id;
}

export interface LineDiff {
  add: LineDraft[];
  change: LineChange[];
  remove: Id[];
}

export interface KindSpec<V extends Values = Values> {
  kind: AddableKind;
  /** The name of the form: "Data field". */
  title: string;
  /** One plain sentence under the name when the person picks a kind. */
  blurb: string;
  /** The box for the name asks for this ("Name of the role"). */
  nameLabel: string;
  nameHint?: string;
  definitionHint?: string;
  /** The name and the definition may be left empty: the spec makes them from the answers. */
  autoText?: (values: V) => { name: string; definition: string } | null;
  empty(draft?: AddByHandDraft | null): V;
  fromElement(row: ElementRow, lines: readonly LineRow[]): V;
  validate(values: V, mode: Mode): Errors;
  /** The content model of the kind (2.4.4). `base` is the content of the element being changed, so keys the form does not cover survive. */
  toContent(values: V, base?: Record<string, Json>): Record<string, Json>;
  /** Lines that go with a new element (add mode). */
  lines?(values: V): LineDraft[];
  /** Lines to add, change and remove on an element being changed (edit mode). */
  diffLines?(values: V, original: readonly LineRow[]): LineDiff;
  /** The kinds of drawing the element is shown on (add mode); the server writes the "is shown on" links. */
  drawnOn?(values: V): Id[];
  Fields: ComponentType<FieldsProps<V>>;
}

/** Specs are written with their own value types; the registry holds them with loose ones. */
export function defineSpec<V extends Values>(spec: KindSpec<V>): KindSpec {
  return spec as unknown as KindSpec;
}

/** A small helper for reading a loose content object. */
export function rec(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

export function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function bool(value: unknown): boolean {
  return value === true;
}

export function ids(value: unknown): Id[] {
  return Array.isArray(value) ? value.filter((entry): entry is Id => typeof entry === "string") : [];
}

export function opt(value: unknown): string | null {
  const text = typeof value === "string" ? value.trim() : "";
  return text ? text : null;
}

/** Drop the keys with no value (null or undefined), for content that may leave a key out. */
export function compact<T extends Record<string, unknown>>(obj: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) if (value !== undefined) out[key] = value;
  return out as T;
}
